import { useEffect, useMemo } from 'react';
import { ButtonProps } from 'primereact/button';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ButtonSize,
  LightButton,
  ButtonVariant,
  PrimaryButton,
} from '@uni-of-exeter/digital.lib.react.ui.kit';

import './styles/your-learning-nav.css';
import { useErrorContext } from '../error';
import { APP_URLS } from '../../routes/paths';
import { APP_TEXT } from '../../const/app-text';
import { useGetHistory } from '../../hooks/query/learning-api/use-quiz-session';

const text = APP_TEXT.components.navBar;

interface LearningNavButtonProps extends ButtonProps {
  url: string;
  pathname: string;
  onClick?: () => void;
  isDisabled?: boolean;
}

const LearningNavButton = ({
  url,
  pathname,
  onClick,
  children,
  isDisabled,
  ...otherProps
}: LearningNavButtonProps) => {
  const isSelected = new URL(`your-learning/${url}`, 'https://placeholder').pathname === pathname;

  return (
    <LightButton
      className={`subNavButton ${isSelected ? 'active' : 'inactive'} ${isDisabled ? 'disabled' : ''}`}
      onClick={onClick}
      variant={ButtonVariant.Outline}
      buttonSize={ButtonSize.Small}
      {...otherProps}
    >
      {children}
    </LightButton>
  );
};

const getIconSrc = (baseName: string, isActive: boolean) =>
  `/assets/your-learning/${baseName}${isActive ? '-active' : ''}.svg`;

const isUrlSelected = (url: string, pathname: string) =>
  new URL(`your-learning/${url}`, 'https://placeholder').pathname === pathname;

interface HistoryGatedButtonProps {
  ariaLabel: string;
  url: string;
  pathname: string;
  iconName: string;
  label: string;
  isLoading: boolean;
  isAvailable: boolean;
  showNewBadge: boolean;
  onNavigate: () => void;
}

const HistoryGatedButton = ({
  ariaLabel,
  url,
  pathname,
  iconName,
  label,
  isLoading,
  isAvailable,
  showNewBadge,
  onNavigate,
}: HistoryGatedButtonProps) => {
  const isDisabled = !isLoading && !isAvailable;

  return (
    <LearningNavButton
      aria-label={ariaLabel}
      url={url}
      pathname={pathname}
      onClick={() => isAvailable && onNavigate()}
      role="menuitem"
      isDisabled={isDisabled}
    >
      <img
        className="yourLearningNavIcon"
        src={
          isDisabled
            ? '/assets/your-learning/eye-closed-disabled.svg'
            : getIconSrc(iconName, isUrlSelected(url, pathname))
        }
        alt={`Navigate to ${label}`}
      />
      {label} {showNewBadge && <span className="newBadge">{text.new}</span>}
    </LearningNavButton>
  );
};
export const YourlearningNavBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: history, isError: historyIsError, isLoading: historyIsLoading } = useGetHistory();
  const { setError } = useErrorContext();

  useEffect(() => {
    if (historyIsError) {
      setError({
        code: 500,
        message: 'Failed to fetch history',
      });
    }
  }, [historyIsError, setError]);

  const isHistoryAvailable = !!(history && history.length > 0);

  const showNewBadge = useMemo(() => {
    if (!history || history.length === 0) return false;
    const lastCompleted = history[history.length - 1]?.completed;
    if (!lastCompleted) return false;
    const completedTime = new Date(lastCompleted).getTime();
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return completedTime > twentyFourHoursAgo;
  }, [history]);

  return (
    <nav
      className="subNavWrapper"
      aria-label={text.ariaLabels.yourlearningNav}
    >
      <div
        className="topSubNav"
        role="menubar"
      >
        <LearningNavButton
          aria-label={text.ariaLabels.bookmarkedResouces}
          url={APP_URLS.bookmarks()}
          pathname={pathname}
          onClick={() => navigate(APP_URLS.bookmarks())}
          role="menuitem"
        >
          <img
            className="yourLearningNavIcon"
            src={getIconSrc('bookmark-icon', isUrlSelected(APP_URLS.bookmarks(), pathname))}
            alt="Navigate to Saved resources"
          />
          {text.bookmarkedResources}
        </LearningNavButton>

        <LearningNavButton
          aria-label={text.ariaLabels.userProgress}
          url={APP_URLS.learningProgress()}
          pathname={pathname}
          onClick={() => navigate(APP_URLS.learningProgress())}
          role="menuitem"
        >
          <img
            className="yourLearningNavIcon"
            src={getIconSrc('progress-icon', isUrlSelected(APP_URLS.learningProgress(), pathname))}
            alt="Navigate to Learning progress"
          />
          {text.userProgress}
        </LearningNavButton>

        <HistoryGatedButton
          ariaLabel={text.ariaLabels.skillStats}
          url={APP_URLS.stats()}
          pathname={pathname}
          iconName="stats-icon"
          label={text.skillStats}
          isLoading={historyIsLoading}
          isAvailable={isHistoryAvailable}
          showNewBadge={showNewBadge}
          onNavigate={() => navigate(APP_URLS.stats())}
        />

        <HistoryGatedButton
          ariaLabel={text.ariaLabels.recommendation}
          url={APP_URLS.recommendations()}
          pathname={pathname}
          iconName="recommended-icon"
          label={text.dashboard}
          isLoading={historyIsLoading}
          isAvailable={isHistoryAvailable}
          showNewBadge={showNewBadge}
          onNavigate={() => navigate(APP_URLS.recommendations())}
        />
      </div>

      {!historyIsLoading && !isHistoryAvailable && (
        <div
          className="rightSubNav"
          role="menubar"
        >
          <PrimaryButton
            onClick={() => navigate(APP_URLS.introPage())}
            buttonSize={ButtonSize.Small}
            aria-label={text.ariaLabels.skillsQuiz}
            className="takeQuizBtn"
            role="menuitem"
          >
            <img
              className="yourLearningNavIcon"
              src="/assets/your-learning/eye-closed.svg"
              alt="Navigate to Self-Assessment Quiz"
            />
            {text.statsUnavailableMessage}
          </PrimaryButton>
        </div>
      )}
    </nav>
  );
};
